<template>
    <Head :title="pageTitle" />
    <RaterChrome :show-anonymous="!invite?.is_authenticated">
        <div v-if="loading" class="scaffold-body-small text-ink-muted">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <AssessmentGroupedSections
            v-else-if="usesPagedRenderer"
            :response-id="responseId"
            :questions="allQuestions"
            :sections="allSections"
            :display-mode="displayMode"
            :questions-per-page="questionsPerPage"
            :allow-score-examples="allowScoreExamples"
            :allow-not-observed="allowNotObserved"
            :invite-token="props.invite_token"
            :accent-color="ACCENT_GREEN"
            :accent-surface-color="ACCENT_GREEN_SURFACE"
            @submitted="onGroupedSubmitted"
            @exit="goToPreviousStep" />
        <template v-else-if="question">
            <header class="flex flex-col gap-4">
                <h1 class="display-serif text-ink">{{ pageTitle }}</h1>
                <HorizontalDivider as="hr" spacing="none" />
                <div class="flex flex-col gap-2">
                    <BaseProgressBar :value="progressPct" size="xs" :aria-label="`Progress: ${progressPct}%`" :color="ACCENT_GREEN" />
                    <div class="scaffold-details-small text-ink-muted">{{ progressLabel }}</div>
                </div>
            </header>

            <div v-if="currentTopSection && !showingSectionIntro" class="flex items-baseline justify-between gap-3">
                <h2 class="body-bold text-ink leading-tight">{{ currentTopSection.title }}</h2>
                <AssessmentEyebrow v-if="currentChildSection">{{ currentChildSection.title }}</AssessmentEyebrow>
            </div>

            <SectionIntroCard v-if="showingSectionIntro && currentTopSection" :section="currentTopSection" :question-count="currentTopSectionQuestionCount" />

            <QuestionCard
                v-else
                :question="question"
                :score="score"
                :text="text"
                :not-observed="notObserved"
                :allow-score-examples="allowScoreExamples"
                :allow-not-observed="allowNotObserved"
                :show-unanswered="showValidation && isCurrentUnanswered"
                :accent-color="ACCENT_GREEN"
                :accent-surface-color="ACCENT_GREEN_SURFACE"
                @update:score="setScore"
                @update:text="text = $event"
                @update:not-observed="setNotObserved"
                @update:busy="composerBusy = $event" />

            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

            <div class="flex items-center justify-between">
                <BaseButton theme="tertiary" :disabled="composerBusy" @click="goPrev"> <i class="bi bi-chevron-left" aria-hidden="true"></i> Back </BaseButton>
                <BaseButton v-if="showingSectionIntro" theme="primary" class="inline-flex items-center gap-1" @click="dismissSectionIntro">
                    Begin section <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton v-else-if="!isLast" theme="primary" :disabled="saving || composerBusy" @click="goNext"> Next </BaseButton>
                <BaseButton v-else theme="primary" :disabled="submitting || submitted || saving || composerBusy" @click="onSubmit">
                    {{ submitting ? "Submitting…" : "Submit" }}
                </BaseButton>
            </div>
            <div v-if="composerBusy && !showingSectionIntro" class="scaffold-details-small text-ink-muted text-right -mt-3">Stop recording to continue.</div>
        </template>
        <ConfirmationDialog
            v-model:open="showSubmitConfirm"
            title="Submit your responses?"
            confirm-label="Submit responses"
            cancel-label="Keep editing"
            :confirm-disabled="submitting"
            :close-on-confirm="false"
            @confirm="confirmSubmit">
            <template #content>
                <div class="body text-ink-muted">Once you submit, <strong class="text-ink">your responses are final and can't be changed.</strong></div>
            </template>
        </ConfirmationDialog>
    </RaterChrome>
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { AssessmentDisplayMode, QuestionWithSaved, ResponseWithQuestions, SectionSummary } from "~vue/api/generated/reflections";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import AssessmentGroupedSections from "~vue/components/AssessmentGroupedSections.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import QuestionCard from "~vue/components/QuestionCard.vue";
import RaterChrome from "~vue/components/RaterChrome.vue";
import SectionIntroCard from "~vue/components/SectionIntroCard.vue";
import { useInviteLoader } from "~vue/composables/useInviteLoader";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import {
    childSectionForCode,
    DEFAULT_QUESTIONS_PER_PAGE,
    opensTopSection,
    questionCountForTopSection,
    rendersQuestionPages,
    sectionsByCode as toSectionsByCode,
    topSectionForCode,
} from "~vue/utils/assessmentSections";
import { isRequiredUnanswered } from "~vue/utils/assessmentValidation";
import { logUserInteraction } from "~vue/utils/logUtils";
import { markPagePublic } from "~vue/utils/sessionEnded";
import { CONNECTION_SUBMIT_MESSAGE, isNetworkError } from "~vue/utils/threeSixty";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

interface Props {
    invite_token: string;
    is_anonymous: boolean;
    question_index: number;
}

const props = defineProps<Props>();

// An external rater has no account to log back into, so a rejected token there must stay silent.
// An internal rater is signed in, and a real session expiry must still offer them recovery.
if (props.is_anonymous) {
    markPagePublic();
}

// Team-family green accent for the progress bar, selected score tile and the
// "not applicable" pill. The status tokens are deliberate rather than
// coincidental: the ledger gives green to team-reflect and to done status
// alike, so the two should move together if the ramp is ever retuned. The pale
// surface keeps the selected tile readable, since full-strength green is too
// dark behind the tile number.
const ACCENT_GREEN = "var(--color-status-success)";
const ACCENT_GREEN_SURFACE = "var(--color-status-success-soft)";

const {
    invite,
    loading: inviteLoading,
    loadError: inviteLoadError,
} = useInviteLoader(props.invite_token, {
    redirectIfResponded: `/reflections/360/r/${props.invite_token}/done`,
    redirectIfCycleClosed: `/reflections/360/r/${props.invite_token}/`,
});

const pageTitle = computed(() => {
    const firstName = invite.value?.subject_first_name?.trim();
    return firstName ? `Feedback for ${firstName}` : "Feedback";
});

const responseId = ref<string>("");
const allQuestions = ref<QuestionWithSaved[]>([]);
const allSections = ref<SectionSummary[]>([]);
const displayMode = ref<AssessmentDisplayMode>("one_per_page");
const questionsPerPage = ref(DEFAULT_QUESTIONS_PER_PAGE);
const allowScoreExamples = ref(true);
const allowNotObserved = ref(true);
const usesPagedRenderer = computed(() => rendersQuestionPages(displayMode.value));
const responseLoading = ref(true);
const responseLoadError = ref<string | null>(null);
const score = ref<number | null>(null);
const text = ref<string>("");
const notObserved = ref(false);
const error = ref<string | null>(null);
const submitting = ref(false);
const saving = ref(false);
const showSubmitConfirm = ref(false);
// Latched once the response is submitted. The Inertia visit below unmounts this
// page, but only eventually — until then this keeps the dialog and the button
// from starting a second submit.
const submitted = ref(false);
// Mirrors the composer's recording/transcribing state so navigation buttons
// can be disabled while a recording is in flight (LAB-491).
const composerBusy = ref(false);
// True while the rater is on the section-intro card for the current question's section.
// Resets to true automatically each time the rater crosses into a new section, and
// is dismissed by clicking "Begin section".
const showingSectionIntro = ref(false);

const loading = computed(() => inviteLoading.value || responseLoading.value);
const loadError = computed(() => inviteLoadError.value || responseLoadError.value);

// Local index for client-side navigation. Seeded from the prop (1-based URL
// ordinal) on mount, then managed entirely in-memory.
const currentIndex = ref(props.question_index - 1);
const question = computed<QuestionWithSaved | null>(() => allQuestions.value[currentIndex.value] ?? null);
const isLast = computed(() => currentIndex.value === allQuestions.value.length - 1);
const progressPct = computed(() => {
    if (allQuestions.value.length === 0) {
        return 0;
    }
    return Math.round(((currentIndex.value + 2) / (allQuestions.value.length + 1)) * 100);
});

const sectionsByCode = computed(() => toSectionsByCode(allSections.value));

const currentTopSection = computed<SectionSummary | null>(() => topSectionForCode(sectionsByCode.value, question.value?.section_code));

const currentChildSection = computed<SectionSummary | null>(() => childSectionForCode(sectionsByCode.value, question.value?.section_code));

const currentTopSectionQuestionCount = computed<number>(() => questionCountForTopSection(allQuestions.value, currentTopSection.value?.code, sectionsByCode.value));

const progressLabel = computed(() => {
    if (showingSectionIntro.value) {
        return "About to start this section";
    }
    return `Question ${currentIndex.value + 1} of ${allQuestions.value.length}`;
});

/** Sync local draft refs from the question at currentIndex. */
function syncFromQuestion() {
    const current = allQuestions.value[currentIndex.value];
    if (current) {
        score.value = current.saved.score ?? null;
        text.value = current.saved.text ?? "";
        notObserved.value = current.saved.not_observed ?? false;
    }
    showValidation.value = false;
    error.value = null;
}

/** Push a history entry so browser back/forward navigates between questions. */
function syncUrl() {
    const url = `/reflections/360/r/${props.invite_token}/q/${currentIndex.value + 1}`;
    globalThis.history.pushState(globalThis.history.state, "", url);
}

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/invites/${props.invite_token}/response`).post().json();
    responseLoading.value = false;
    if (fetchError.value) {
        responseLoadError.value = (data.value as { error?: string })?.error || "Could not load question.";
        return;
    }
    const response = (data.value as { response: ResponseWithQuestions }).response;
    // Navigating straight to a question URL skips the relationship step, and a
    // submitted response can no longer be categorized. Send the rater back to
    // pick one first rather than let them answer everything and be refused.
    if (response.relationship === null && response.submitted_at === null) {
        router.visit(`/reflections/360/r/${props.invite_token}/relationship`);
        return;
    }
    responseId.value = response.id;
    allQuestions.value = response.questions;
    allSections.value = response.sections ?? [];
    displayMode.value = response.display_mode ?? "one_per_page";
    questionsPerPage.value = response.questions_per_page ?? DEFAULT_QUESTIONS_PER_PAGE;
    allowScoreExamples.value = response.allow_score_examples ?? true;
    allowNotObserved.value = response.allow_not_observed ?? true;

    syncFromQuestion();

    if (allQuestions.value[currentIndex.value] && shouldShowIntroFor(currentIndex.value)) {
        showingSectionIntro.value = true;
    }
});

watch(currentIndex, () => {
    syncFromQuestion();
    syncUrl();
    globalThis.scrollTo({ top: 0 });
});

function onPopState() {
    const match = globalThis.location.pathname.match(/\/q\/(\d+)$/);
    if (match) {
        const idx = parseInt(match[1], 10) - 1;
        if (idx >= 0 && idx < allQuestions.value.length && idx !== currentIndex.value) {
            currentIndex.value = idx;
        }
    }
}
globalThis.addEventListener("popstate", onPopState);
onUnmounted(() => globalThis.removeEventListener("popstate", onPopState));

function shouldShowIntroFor(index: number): boolean {
    return opensTopSection(allQuestions.value, index, sectionsByCode.value);
}

function dismissSectionIntro() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_SECTION_STARTED, {
        section_code: currentTopSection.value?.code ?? null,
        section_question_count: currentTopSectionQuestionCount.value,
        total_questions: allQuestions.value.length,
    });
    showingSectionIntro.value = false;
}

function setScore(n: number) {
    score.value = n;
    notObserved.value = false;
}

function setNotObserved(v: boolean) {
    notObserved.value = v;
    if (v) {
        score.value = null;
    }
}

async function save(): Promise<boolean> {
    if (!question.value) {
        return true;
    }
    saving.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/responses/${responseId.value}/answers?invite_token=${props.invite_token}`)
            .post(
                JSON.stringify({
                    question_id: question.value.id,
                    score: score.value,
                    text: text.value,
                    not_observed: notObserved.value,
                }),
                "application/json",
            )
            .json();
        if (fetchError.value) {
            error.value = "Could not save your answer. Please try again.";
            return false;
        }
        // Keep the local saved state in sync so validation works.
        const q = allQuestions.value[currentIndex.value];
        if (q) {
            q.saved = { score: score.value, text: text.value, not_observed: notObserved.value };
        }
        return true;
    } finally {
        saving.value = false;
    }
}

// Build a snapshot of the current question's draft so we can validate against
// the live local state (score/text/notObserved live in refs until save()).
function currentDraftSnapshot(): QuestionWithSaved | null {
    if (!question.value) {
        return null;
    }
    return { ...question.value, saved: { score: score.value, text: text.value, not_observed: notObserved.value } };
}

/** Validate the current question's draft. Returns true if valid. */
function validateCurrentDraft(action: "continuing" | "submitting"): boolean {
    const draft = currentDraftSnapshot();
    if (draft && isRequiredUnanswered(draft)) {
        showValidation.value = true;
        error.value = `Please answer this required question before ${action}.`;
        return false;
    }
    showValidation.value = false;
    error.value = null;
    return true;
}

async function goNext() {
    if (!validateCurrentDraft("continuing")) {
        return;
    }
    if (!(await save())) {
        return;
    }
    currentIndex.value += 1;
    showingSectionIntro.value = shouldShowIntroFor(currentIndex.value);
}

async function goPrev() {
    if (showingSectionIntro.value) {
        showingSectionIntro.value = false;
        return;
    }
    if (!(await save())) {
        return;
    }
    if (currentIndex.value === 0) {
        goToPreviousStep();
        return;
    }
    currentIndex.value -= 1;
    showingSectionIntro.value = false;
}

const showValidation = ref(false);

const isCurrentUnanswered = computed(() => {
    const draft = currentDraftSnapshot();
    if (!draft) {
        return false;
    }
    return isRequiredUnanswered(draft);
});

async function onSubmit() {
    if (submitting.value || submitted.value) {
        return;
    }
    if (!validateCurrentDraft("submitting")) {
        return;
    }
    if (!(await save())) {
        return;
    }
    showSubmitConfirm.value = true;
}

async function confirmSubmit() {
    if (submitting.value || submitted.value) {
        return;
    }
    submitting.value = true;
    try {
        const { error: fetchError, data } = await useFetch(`/v1/reflections/responses/${responseId.value}/submit?invite_token=${props.invite_token}`).post().json();
        if (fetchError.value) {
            error.value = isNetworkError(fetchError.value) ? CONNECTION_SUBMIT_MESSAGE : (data.value as { error?: string })?.error || "Could not submit.";
            return;
        }
        submitted.value = true;
        const totalQuestions = allQuestions.value.length;
        const answeredCount = allQuestions.value.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
        const textAnswerCount = allQuestions.value.filter((q) => (q.saved.text ?? "").trim().length > 0).length;
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_RESPONSE_SUBMITTED, {
            total_questions: totalQuestions,
            answered_count: answeredCount,
            text_answer_count: textAnswerCount,
        });
        router.visit(`/reflections/360/r/${props.invite_token}/done`);
    } finally {
        submitting.value = false;
        // The dialog is `:close-on-confirm="false"`, so it stays up until we
        // close it — a slow, cancelled or failed visit must not leave it open.
        showSubmitConfirm.value = false;
    }
}

// Grouped renderer owns its own save/submit; the host keeps the submit
// analytics + redirect so behavior matches the one-per-page flow.
function onGroupedSubmitted() {
    const totalQuestions = allQuestions.value.length;
    const answeredCount = allQuestions.value.filter((q) => q.saved.score !== null || q.saved.not_observed || (q.saved.text ?? "").trim().length > 0).length;
    const textAnswerCount = allQuestions.value.filter((q) => (q.saved.text ?? "").trim().length > 0).length;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_RESPONSE_SUBMITTED, {
        total_questions: totalQuestions,
        answered_count: answeredCount,
        text_answer_count: textAnswerCount,
    });
    router.visit(`/reflections/360/r/${props.invite_token}/done`);
}

// The step before the questions: the relationship page, unless the subject
// already categorized this rater at nomination, in which case the landing page.
function goToPreviousStep() {
    if (invite.value?.relationship_prefilled) {
        router.visit(`/reflections/360/r/${props.invite_token}/`);
        return;
    }
    router.visit(`/reflections/360/r/${props.invite_token}/relationship`);
}
</script>
